import { IconKey, IconServer } from '@developerz/ui';
import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import { ago } from '../lib/time';
import {
  type ApiToken,
  isExpired,
  isRevocableHere,
  isRevoked,
  tokenKind,
  tokenRevokeCall,
} from '../lib/tokens';
import { CopyAsCall } from './CopyAsCall';
import { StatusPill } from './StatusPill';
import { Button, buttonClass } from './ui/Button';

// One API token as a row-list row (spec §3.15): icon tile + stacked
// name/prefix/scopes/usage lines, ghost actions right. Presenter.
//
// THE NAME IS THE ROW'S TITLE, and the prefix is demoted beneath it (#2828). The
// prefix is the same literal `dev_pat_` on EVERY token this platform has ever
// minted, so a roster led by it is a column of identical strings — which is what
// it was, and why "which of these is my CI job?" had no answer on this screen.
// A row nobody named says so in the reader's words: `name` is null on every token
// minted before the field existed and on every box credential, and "Unnamed
// token" is a label this app writes, never one the API stores.
//
// It shows a PREFIX, never a token: the API keeps only a sha256 hash, so the
// plaintext exists exactly once — in the mint notice. "Last used" is therefore
// the only way to tell a live integration from a forgotten credential, which is
// what makes revoking one a decision rather than a guess.
//
// STATE IS A LADDER, and its order is the row's: revoked (a human killed it)
// outranks expired (the clock did), because only the first is a decision
// someone made. Expired is a THIRD state and not a shade of active — the API
// answers 401 for it — so a token that has run out must not keep reading
// "Active" here, which is exactly what it did while the list carried no
// `expires_iso` at all.

export type TokenRowProps = {
  readonly row: ApiToken;
  /** Opens the rename dialog. Omitted → no rename control (read-only caller). */
  readonly onRename?: (row: ApiToken) => void;
  /** Opens the revoke confirm. Omitted → no revoke control (read-only caller). */
  readonly onRevoke?: (row: ApiToken) => void;
};

export function TokenRow(props: TokenRowProps): JSX.Element {
  const box = (): boolean => tokenKind(props.row) === 'box';

  return (
    <li class="row-list__row token-entry" data-token-id={props.row.id}>
      <div class="row-list__lead">
        <span class="row-list__tile">{box() ? <IconServer /> : <IconKey />}</span>
        <div class="token-entry__lines">
          <p class="token-entry__name">
            <span class="token-entry__label">{props.row.name ?? t('account_tokens.unnamed')}</span>
            <Show
              when={isRevoked(props.row)}
              fallback={
                <Show
                  when={isExpired(props.row)}
                  fallback={
                    <StatusPill tone="success">{t('account_tokens.state_active')}</StatusPill>
                  }
                >
                  <StatusPill tone="warning">{t('account_tokens.state_expired')}</StatusPill>
                </Show>
              }
            >
              <StatusPill tone="danger">{t('account_tokens.state_revoked')}</StatusPill>
            </Show>
            <Show when={box()}>
              <StatusPill tone="neutral">{t('account_tokens.kind_box')}</StatusPill>
            </Show>
          </p>
          <code class="token-entry__prefix">{props.row.prefix}…</code>
          <ul class="token-entry__tags">
            <For each={props.row.scopes}>
              {(scope) => (
                <li>
                  <span class="badge badge--outline">{scope}</span>
                </li>
              )}
            </For>
          </ul>
          <p class="token-entry__meta">
            <span>{t('account_tokens.created', { when: ago(props.row.created_iso) })}</span>
            <span>
              <Show when={props.row.last_used_iso} fallback={t('account_tokens.never_used')} keyed>
                {(when) => t('account_tokens.last_used', { when: ago(when) })}
              </Show>
            </span>
            {/* Absent entirely on a permanent token: "Expires never" is a line
                that says nothing on all but the OAuth-granted rows. */}
            <Show when={props.row.expires_iso} keyed>
              {(when) => (
                <span>
                  {isExpired(props.row)
                    ? t('account_tokens.expired', { when: ago(when) })
                    : t('account_tokens.expires', { when: ago(when) })}
                </span>
              )}
            </Show>
          </p>
        </div>
      </div>
      <div class="token-entry__actions">
        {/* A box's credential is revoked WITH its box, from the fleet — killing
            it here would strand a runner the mothership still counts as enrolled. */}
        <Show when={box()}>
          <A class={buttonClass({ variant: 'ghost', size: 'sm' })} href={orgHref('/fleet')}>
            {t('account_tokens.manage_in_fleet')}
          </A>
        </Show>
        {/* Offered on a REVOKED row too, and on a box credential: this write
            moves the label and nothing else, and a dead token nobody can name is
            exactly the row a reader wants to annotate before leaving it there. */}
        <Show when={props.onRename}>
          <Button variant="ghost" size="sm" onClick={() => props.onRename?.(props.row)}>
            {t('account_tokens.rename')}
          </Button>
        </Show>
        <Show when={props.onRevoke && isRevocableHere(props.row)}>
          <button
            type="button"
            class={`${buttonClass({ variant: 'ghost', size: 'sm' })} token-entry__revoke`}
            onClick={() => props.onRevoke?.(props.row)}
          >
            {t('account_tokens.revoke')}
          </button>
          {/* Inside the same gate as the button: a box credential is revoked
              with its box, and this row must not teach a call that strands one. */}
          <CopyAsCall
            calls={[{ label: t('account_tokens.revoke'), call: tokenRevokeCall(props.row.id) }]}
          />
        </Show>
      </div>
    </li>
  );
}
